import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useBrand } from '@/contexts/brand/BrandContext';

/**
 * Reactively syncs brand UUID into URL query params.
 *
 * Doesn't care HOW brand was found (subdomain, token, resource lookup,
 * embed param, localStorage). As soon as BrandProvider resolves a UUID,
 * the URL gets it via shallow replace (no history entry, no re-render).
 *
 * Mount once in _app.jsx after BrandProvider.
 */
export const useBrandUrlSync = () => {
  const router = useRouter();
  const { brand } = useBrand();
  const brandUuid = brand?.uuid ?? brand?.brandUuid;
  const hasReplacedRef = useRef(false);

  useEffect(() => {
    if (!brandUuid || !router.isReady) return;
    if (router.query.brandUuid === brandUuid) return;

    // Only replace once per brand resolution to avoid loops
    if (hasReplacedRef.current) return;
    hasReplacedRef.current = true;

    // Add brandUuid alongside existing params — don't strip brandID or brand,
    // embed pages depend on brandID staying in the URL for EmbedAuthGuard.
    router.replace(
      { pathname: router.pathname, query: { ...router.query, brandUuid } },
      undefined,
      { shallow: true },
    );
  }, [brandUuid, router.isReady]);

  // Reset when brand changes (e.g., switching brands)
  useEffect(() => {
    hasReplacedRef.current = false;
  }, [brandUuid]);
};
